import { useCallback, useMemo } from 'react';

import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useFiatBalance } from '@sorare/core/src/hooks/wallets/useFiatBalance';
import { MonetaryAmount } from '@sorare/core/src/lib/monetaryAmount';

type Args =
  | {
      monetaryAmountToPayWithFiatWallet: MonetaryAmount;
      monetaryAmountToPayWithEthWallet: MonetaryAmount;
    }
  | MonetaryAmount;

export const useHasInsufficientFundsInWallets = () => {
  const { currentUser } = useCurrentUserContext();
  const { availableBalanceInCents, fiatCurrency } = useFiatBalance();

  const availableBalance = useMemo(
    () => currentUser?.availableBalance || 0n,
    [currentUser]
  );

  const diff = (balance: bigint, weiAmountToPay: bigint) =>
    weiAmountToPay - balance;

  return useCallback(
    (args: Args) => {
      const monetaryAmountToPayWithEthWallet =
        'monetaryAmountToPayWithEthWallet' in args
          ? args.monetaryAmountToPayWithEthWallet
          : args;
      const monetaryAmountToPayWithFiatWallet =
        'monetaryAmountToPayWithFiatWallet' in args
          ? args.monetaryAmountToPayWithFiatWallet
          : args;

      const insufficientFundsInEthWallet =
        availableBalance < monetaryAmountToPayWithEthWallet.wei;
      const diffInWeiForEthWallet =
        (insufficientFundsInEthWallet &&
          diff(availableBalance, monetaryAmountToPayWithEthWallet.wei)) ||
        undefined;
      const insufficientFundsInFiatWallet =
        availableBalanceInCents <
        monetaryAmountToPayWithFiatWallet.readAmount(fiatCurrency);
      const diffInFiatCentsForFiatWallet =
        (insufficientFundsInFiatWallet &&
          availableBalanceInCents -
            monetaryAmountToPayWithFiatWallet.readAmount(fiatCurrency)) ||
        undefined;
      return {
        insufficientFundsInEthWallet,
        diffInWeiForEthWallet,
        insufficientFundsInFiatWallet,
        diffInFiatCentsForFiatWallet,
      };
    },
    [availableBalance, availableBalanceInCents, fiatCurrency]
  );
};
